import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { debugLog } from '@/debug'
import { safeStorage, STORAGE_KEYS } from '@/utils/storage'

// In production, use same-origin proxy (/api/...) to avoid CORS.
// In dev, point directly at the API server.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const REFRESH_PATH = '/api/v1/auth/refresh'

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

client.interceptors.request.use((config) => {
  const token = safeStorage.get(STORAGE_KEYS.ACCESS_TOKEN)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  debugLog.add('network', `→ ${config.method?.toUpperCase()} ${config.baseURL ?? ''}${config.url ?? ''}`)
  return config
})

// Coalesce concurrent refresh attempts into a single in-flight promise.
// Without this, multiple parallel 401s would each fire a refresh, causing
// race conditions and potentially infinite loops.
let refreshInFlight: Promise<string> | null = null

async function performRefresh(): Promise<string> {
  const refreshToken = safeStorage.get(STORAGE_KEYS.REFRESH_TOKEN)
  if (!refreshToken) throw new Error('no refresh token')

  debugLog.add('network', '→ POST /api/v1/auth/refresh (auto)')
  const refreshUrl = API_BASE_URL ? `${API_BASE_URL}${REFRESH_PATH}` : REFRESH_PATH
  const { data } = await axios.post(refreshUrl, { refresh_token: refreshToken })
  safeStorage.set(STORAGE_KEYS.ACCESS_TOKEN, data.access_token)
  safeStorage.set(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token)
  debugLog.add('info', 'Token refreshed')
  return data.access_token
}

function clearSession(): void {
  safeStorage.remove(STORAGE_KEYS.ACCESS_TOKEN)
  safeStorage.remove(STORAGE_KEYS.REFRESH_TOKEN)
  safeStorage.remove(STORAGE_KEYS.USER)
}

client.interceptors.response.use(
  (response) => {
    debugLog.add('network', `← ${response.status} ${response.config.url ?? ''}`)
    return response
  },
  async (error: AxiosError) => {
    const status = error.response?.status ?? 'NO_RESPONSE'
    const url = error.config?.url ?? '?'
    debugLog.add('error', `← ${status} ${url}: ${error.message}`)

    const originalRequest = error.config as RetryableConfig | undefined
    const isRefreshCall = originalRequest?.url?.includes(REFRESH_PATH)

    // Don't try to refresh on the refresh call itself (would infinite loop)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshCall
    ) {
      originalRequest._retry = true

      try {
        // Reuse in-flight refresh if one is already running
        if (!refreshInFlight) {
          refreshInFlight = performRefresh().finally(() => {
            refreshInFlight = null
          })
        }
        const newToken = await refreshInFlight
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }
        return client(originalRequest)
      } catch {
        debugLog.add('error', 'Token refresh failed, clearing session')
        clearSession()
        // Reload only outside iframe-restricted contexts
        try {
          window.location.reload()
        } catch {
          // Ignore — Office.js sandbox may block reload
        }
      }
    }

    return Promise.reject(error)
  },
)

export default client
